import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { clusterById } from '../../../data/teaser';
import { proactive, proactivePlans, snak2Images } from '../../../data/snak2';
const EASE = [0.23, 1, 0.32, 1] as const;
interface ProactiveScreenProps {
  /** Storyboard only — renders the screen with a specific cluster selected. */
  previewIndex?: number;
}
export function ProactiveScreen({
  previewIndex
}: ProactiveScreenProps = {}) {
  const [index, setIndex] = useState(previewIndex ?? 0);
  const plan = proactivePlans[index];
  const cluster = clusterById(plan.clusterId);
  const rows = [{
    label: proactive.watchLabel,
    value: plan.watch
  }, {
    label: proactive.intervalLabel,
    value: plan.interval
  }, {
    label: proactive.triggerLabel,
    value: plan.trigger
  }];
  return <div className="flex flex-1 flex-col" data-id="element-703" data-mp="2g5ob98fpade7">
      <div className="flex items-start gap-3" data-id="element-704" data-mp="2g2inbwit5hk2">
        <motion.h2 initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        ease: EASE
      }} className="flex-1 font-display text-[24px] font-bold uppercase leading-[1] tracking-tight text-ozNavy" data-id="element-705" data-mp="2l2wwxj024zk7">
          {proactive.headline[0]}
          <span className="mt-1 block text-ozBlue" data-id="element-706" data-mp="2v1x1psgtw4es">{proactive.headline[1]}</span>
        </motion.h2>

        <motion.img initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.28,
        delay: 0.08,
        ease: EASE
      }} src={snak2Images.fundus.src} alt={snak2Images.fundus.alt} draggable={false} className="h-[62px] w-[62px] shrink-0 select-none rounded-full border-2 border-paper object-cover shadow-soft" data-id="element-707" data-mp="2r0lhy3kmydei" />
      </div>

      <p className="mt-3 text-[12px] leading-relaxed text-mut" data-id="element-708" data-mp="39w1uq5fcl6nr">{proactive.lead}</p>

      <div className="mt-4 flex gap-1.5" role="tablist" aria-label="Phenotype clusters" data-id="element-709" data-mp="ac7kj8snk6ix">
        {proactivePlans.map((p, i) => {
        const isActive = i === index;
        return <button key={p.clusterId} type="button" role="tab" aria-selected={isActive} onClick={() => setIndex(i)} className={`h-9 flex-1 rounded-lg border font-display text-[12px] font-bold tracking-tight transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozBlue ${isActive ? 'border-ozNavy bg-ozNavy text-paper' : 'border-line bg-ozMist text-ozNavy hover:border-ozBlue'}`} data-id="element-710" data-mp="1zu4gxatz7ctf" data-mpi={i}>
              CL {i + 1}
            </button>;
      })}
      </div>

      <AnimatePresence mode="wait" data-id="element-711" data-mp="1c2z1uxvv0ok6">
        <motion.div key={plan.clusterId} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="mt-3" data-id="element-712" data-mp="1c4a8d5gowaez">
          <p className="text-[11px] leading-snug text-ozBlue" data-id="element-713" data-mp="utwrexku7t54">{cluster.descriptor}</p>
          <p className="mt-1 text-[10.5px] leading-snug text-mut" data-id="element-714" data-mp="pujvzofwhdqx">{cluster.signature}</p>

          <dl className="mt-3 flex flex-col gap-2" data-id="element-715" data-mp="14j6luk35lnwe">
            {rows.map((row, i) => <motion.div key={row.label} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.22,
            delay: 0.05 + i * 0.05,
            ease: EASE
          }} className="border-l-2 border-ozGreen pl-3" data-id="element-716" data-mp="zs8198b4u3nc" data-mpi={i}>
                <dt className="text-[8.5px] uppercase tracking-micro text-mut" data-id="element-717" data-mp="1ni2brj7zbg4f">{row.label}</dt>
                <dd className="mt-0.5 text-[12.5px] font-medium leading-snug text-ozNavy" data-id="element-718" data-mp="1ihxy00yggzhw">
                  {row.value}
                </dd>
              </motion.div>)}
          </dl>
        </motion.div>
      </AnimatePresence>

      <p className="mt-auto pt-4 text-[9.5px] leading-snug text-mut" data-id="element-719" data-mp="3kbdpf02daidx">{proactive.caveat}</p>
    </div>;
}